import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBookingPaymentSchedule,
  createBookingPaymentScheduleEntry,
  updateBookingPaymentScheduleEntry,
} from '@/api/generated/sdk.gen';
import type {
  BookingPaymentSchedule,
  PaymentScheduleEntry,
  CreateScheduleEntryRequest,
  UpdateScheduleEntryRequest,
  SystemDivergence,
} from '@/api/generated/types.gen';

export type {
  BookingPaymentSchedule,
  PaymentScheduleEntry,
  CreateScheduleEntryRequest,
  UpdateScheduleEntryRequest,
  SystemDivergence,
};

// Shared query key builder — the mutations below invalidate exactly this key,
// so it lives once instead of being re-typed at every call site. Exported for
// lib/finance.ts's slice-3 settle mutations (ConfirmReceiptDialog et al.),
// which invalidate a booking's schedule from OUTSIDE this file when a
// caller passes a bookingId — same key, not a re-typed duplicate that could
// drift from this one.
export function scheduleKey(bookingId: string) {
  return ['booking', bookingId, 'payment-schedule'] as const;
}

// The cashflow expectation layer for a booking (ADR-033): expected entries,
// which are reconciled against received payments, plus a received/outstanding
// summary. Loaded lazily by the Payments card on the booking detail page.
export function paymentScheduleQuery(id: string) {
  return queryOptions({
    queryKey: scheduleKey(id),
    queryFn: async ({ signal }) => {
      const res = await getBookingPaymentSchedule({ throwOnError: true, signal, path: { id } });
      return res.data;
    },
    retry: (count, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && status !== 404 && count < 1;
    },
    staleTime: 30_000,
  });
}

// useCreateScheduleEntry — POST .../payment-schedule (OH-124 slice 2, spec §5).
// Requires payments.schedule_write; the caller (ScheduleEditor) gates the UI
// that invokes this on the same permission.
export function useCreateScheduleEntry(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateScheduleEntryRequest) =>
      createBookingPaymentScheduleEntry({ throwOnError: true, path: { id: bookingId }, body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scheduleKey(bookingId) });
    },
  });
}

// useUpdateScheduleEntry — PATCH .../payment-schedule/{entry_id}. One hook
// covers every write shape the editor needs (inline edit, mark received,
// void) — they differ only in which fields the caller populates in `body`.
export function useUpdateScheduleEntry(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, body }: { entryId: string; body: UpdateScheduleEntryRequest }) =>
      updateBookingPaymentScheduleEntry({
        throwOnError: true,
        path: { id: bookingId, entry_id: entryId },
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scheduleKey(bookingId) });
    },
  });
}

// ─── Booking.com card-state note parsing (OH-124 §3.2.1) ──────────────────
//
// billing.Desired (Go) composes a free-text `notes` string for vcc_charge
// entries out of a handful of fixed templates (internal/domain/billing/
// generate.go: applyCardState). Rather than duplicate the status/balance
// facts in a second wire field, the FE parses the same templates back into
// structured data so the card can render them without the "VCC" jargon and
// without a raw log-line look. Any note that does not match a known template
// (an operator's free-text note, a future backend phrasing) returns null —
// the caller falls back to rendering the raw string, so a parser miss never
// drops information.

export interface CardState {
  statusLabel: string;
  balance?: string;
  mismatch: boolean;
  cancellationFee: boolean;
}

const VCC_STATUS_LABEL: Record<string, string> = {
  fully_charged: 'Fully charged',
  funded: 'Funded',
  available: 'Available',
  cancelled: 'Cancelled by Booking.com',
  not_loaded: 'Not loaded',
  unknown: 'Unknown',
};

export function parseCardStateNote(notes: string | null | undefined): CardState | null {
  if (!notes) return null;
  // joinNotes (Go) composes with "; " — "cancellation fee" is always first
  // when present (generate.go:224).
  const cancellationFee = notes.startsWith('cancellation fee');
  const vccPart = notes.split('; ').find((p) => p.startsWith('VCC '));
  if (!vccPart) return null;

  if (vccPart === 'VCC fully charged (Booking.com)') {
    return { statusLabel: VCC_STATUS_LABEL.fully_charged as string, mismatch: false, cancellationFee };
  }
  if (vccPart === 'VCC cancelled by Booking.com') {
    return { statusLabel: VCC_STATUS_LABEL.cancelled as string, mismatch: false, cancellationFee };
  }
  // "VCC available, balance 532.05 EUR" or "...(differs from expected total)".
  const withBalance = /^VCC (\w+), balance ([^()]+?)( \(differs from expected total\))?$/.exec(vccPart);
  if (withBalance) {
    const [, statusKey, balance, mismatchSuffix] = withBalance;
    return {
      statusLabel: (statusKey && VCC_STATUS_LABEL[statusKey]) || (statusKey ?? ''),
      balance: balance?.trim(),
      mismatch: !!mismatchSuffix,
      cancellationFee,
    };
  }
  // Bare "VCC not_loaded" / "VCC unknown" / any future status with no balance.
  const bare = /^VCC (\w+)$/.exec(vccPart);
  if (bare) {
    const statusKey = bare[1];
    return {
      statusLabel: (statusKey && VCC_STATUS_LABEL[statusKey]) || (statusKey ?? ''),
      mismatch: false,
      cancellationFee,
    };
  }
  return null;
}

// formatCardStateLine renders a parsed CardState as ONE plain string (never
// several JSX fragments) so the whole line is a single DOM text node —
// simpler to test and to read than "Card: " + {statusLabel} + conditional
// bits scattered across the JSX.
export function formatCardStateLine(cardState: CardState): string {
  const parts = [`Card: ${cardState.statusLabel}`];
  if (cardState.balance) parts.push(`balance ${cardState.balance}`);
  if (cardState.mismatch) parts.push('differs from expected');
  const line = parts.join(' · ');
  return cardState.cancellationFee ? `Cancellation fee · ${line}` : line;
}
